/**
 * Version management utilities for detecting app updates
 * and displaying current app version across platforms
 */

import { isNativeApp } from './platform';

const LOCAL_BUILD_ID_KEY = 'postpartner_local_build_id';
const LAST_RELOAD_KEY = 'postpartner_last_reload_time';

export interface VersionInfo {
  appVersion: string;
  buildTime: string;
  gitSha: string;
  buildId: string;
}

/**
 * App version display info returned from getAppVersion()
 */
export interface AppVersionInfo {
  version: string;      // e.g., "1.2.0"
  build?: string;       // e.g., "42" (iOS build number)
  displayString: string; // e.g., "v1.2.0" or "v1.2.0 (42)"
}

// Cache for native version to avoid repeated async calls
let cachedNativeVersion: AppVersionInfo | null = null;

/**
 * Get the current app version for display
 * - iOS Native: Uses Capacitor App.getInfo() for version/build
 * - Web: Uses build-time injected VITE_APP_VERSION or version.json
 *
 * Returns a promise since iOS requires async Capacitor call.
 * Results are cached after first call.
 */
export async function getAppVersion(): Promise<AppVersionInfo> {
  // Return cached native version if available
  if (cachedNativeVersion) {
    return cachedNativeVersion;
  }

  if (isNativeApp()) {
    try {
      // Dynamic import to avoid bundling in web builds
      const dynamicImport = new Function('specifier', 'return import(specifier)') as (
        specifier: string
      ) => Promise<unknown>;
      const appModule = await dynamicImport('@capacitor/app').catch(() => null) as {
        App?: { getInfo: () => Promise<{ version: string; build: string; name: string; id: string }> };
      } | null;

      if (appModule?.App) {
        const info = await appModule.App.getInfo();
        cachedNativeVersion = {
          version: info.version,
          build: info.build,
          displayString: `v${info.version} (${info.build})`,
        };
        return cachedNativeVersion;
      }
    } catch {
      // Fall through to web version
    }
  }

  // Web: Use build-time injected version
  const buildVersion = import.meta.env.VITE_APP_VERSION as string | undefined;
  if (buildVersion) {
    return {
      version: buildVersion,
      displayString: `v${buildVersion}`,
    };
  }

  // Fallback: Try to get from version.json (for deployed web builds)
  const remoteVersion = await fetchRemoteVersion();
  if (remoteVersion?.appVersion) {
    return {
      version: remoteVersion.appVersion,
      displayString: `v${remoteVersion.appVersion}`,
    };
  }

  // Last resort fallback
  return {
    version: '1.0.0',
    displayString: 'v1.0.0',
  };
}

/**
 * Fetch remote version info from the server
 * Uses cache: 'no-store' to ensure fresh data
 * Silently returns null in development mode where version.json doesn't exist
 */
export async function fetchRemoteVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      // Silently fail - version.json may not exist in development
      return null;
    }

    // Check content-type to avoid parsing HTML error pages
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      // Server returned HTML (likely 404 page) - silently fail
      return null;
    }

    const data = await response.json();
    return data as VersionInfo;
  } catch {
    // Silently fail - this is expected in development
    return null;
  }
}

/**
 * Get the locally stored build ID
 */
export function getLocalBuildId(): string | null {
  try {
    return localStorage.getItem(LOCAL_BUILD_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Set the local build ID
 */
export function setLocalBuildId(buildId: string): void {
  try {
    localStorage.setItem(LOCAL_BUILD_ID_KEY, buildId);
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * Get the last reload timestamp to prevent rapid reload loops
 */
export function getLastReloadTime(): number {
  try {
    const time = localStorage.getItem(LAST_RELOAD_KEY);
    return time ? parseInt(time, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Set the last reload timestamp
 */
export function setLastReloadTime(): void {
  try {
    localStorage.setItem(LAST_RELOAD_KEY, Date.now().toString());
  } catch {
    // localStorage might be unavailable
  }
}

/**
 * React hook to get app version info
 * Fetches version on mount and caches the result
 */
import { useState, useEffect } from 'react';

export function useAppVersion(): AppVersionInfo | null {
  const [versionInfo, setVersionInfo] = useState<AppVersionInfo | null>(null);

  useEffect(() => {
    let mounted = true;
    getAppVersion().then((info) => {
      if (mounted) {
        setVersionInfo(info);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return versionInfo;
}
